import { useLocation } from "react-router-dom";
import UIContext, {
  currentRouteOptionsI,
  initialContent,
  InitialDataInterface,
} from "./context";
import React, { useRef } from "react";
import useStateRef from "react-usestateref";
import useDevice from "hooks/useDevice";

const UIProvider = ({ children }: { children: React.ReactNode }) => {
  const handleEscape = (e: KeyboardEvent, closeFn: () => void) => {
    if (e.key === "Escape") {
      closeFn();
    }
  };

  const [currentRouteOptions, setCurrentRouteOptions] = React.useState({
    route: "/",
    callback: undefined,
    hasSearchInput: false,
    onSearch: () => {},
    isSearching: false,
    closeDrawerOnRouteChange: true,
  });

  const resetCurrentRouteOptions = () => {
    setCurrentRouteOptions({
      route: "/",
      callback: undefined,
      hasSearchInput: false,
      onSearch: () => {},
      isSearching: false,
      closeDrawerOnRouteChange: true,
    });
  };
  // Overlay
  const [isOverlayOpen, setOverlayOpen] = React.useState(false);
  // Modal
  const [isNestedModalOpen, setNestedModal] = React.useState(false);
  const [nestedModalData, setNestedModalData] = React.useState(initialContent);

  const [isModalOpen, setModalOpen] = React.useState(false);

  const [modalData, setModalData] = React.useState(initialContent);

  const listenersRef = useRef({
    escapeModal: (_e: KeyboardEvent) => {},
    escapeNestedModal: (_e: KeyboardEvent) => {},
    escapeRightDrawer: (_e: KeyboardEvent) => {},
  });

  const openModal = React.useCallback((data: InitialDataInterface) => {
    setModalData({
      ...data,
      isClosableByOverlay: data.isClosableByOverlay ?? true,
    });

    setModalOpen(true);
    setOverlayOpen(true);

    listenersRef.current = {
      ...listenersRef.current,
      escapeModal: (e: KeyboardEvent) => {
        handleEscape(e, async () => {
          if (data?.onClose)
            try {
              await data.onClose();
            } catch (err) {
              console.error(err);
            }

          closeModal();
        });
      },
    };

    document.addEventListener("keydown", listenersRef.current.escapeModal);
  }, []);

  const closeModal = React.useCallback(() => {
    setModalOpen(false);
    setOverlayOpen(false);
    document.removeEventListener("keydown", listenersRef.current.escapeModal);
  }, []);

  const openNestedModal = React.useCallback((data: InitialDataInterface) => {
    setNestedModalData(data);
    setNestedModal(true);
    setOverlayOpen(true);

    listenersRef.current = {
      ...listenersRef.current,
      escapeNestedModal: async (e: KeyboardEvent) => {
        handleEscape(e, async () => {
          if (data?.onClose)
            try {
              await data.onClose();
            } catch (err) {
              console.error(err);
            }

          closeNestedModal();
        });
      },
    };

    document.addEventListener(
      "keydown",
      listenersRef.current.escapeNestedModal,
    );
  }, []);

  const closeNestedModal = React.useCallback(() => {
    setNestedModal(false);
    setOverlayOpen(false);

    document.removeEventListener(
      "keydown",
      listenersRef.current.escapeNestedModal,
    );
  }, []);
  // Action bar
  const [isActionBarOpen, setIsActionBarOpen] = React.useState(false);
  const [actionBarData, setActionBarData] = React.useState(initialContent);

  const openActionBar = React.useCallback((data: InitialDataInterface) => {
    setActionBarData(data);
    setIsActionBarOpen(true);
  }, []);

  const closeActionBar = React.useCallback(() => {
    setIsActionBarOpen(false);
  }, []);

  // Sidebar
  const [isSidebarOpen, setSidebarOpen, isSidebarOpenRef] = useStateRef(false);
  const { isMobile, isTablet } = useDevice();

  const [sidebarData, setSidebarData] = React.useState<
    React.ReactNode | undefined
  >();

  const [isSidebarShrunk, setSidebarShrunk] = React.useState(() => {
    if (isMobile) {
      return false;
    }

    return isTablet;
  });

  const toggleSidebar = (state?: boolean) => {
    let newState = state;
    if (state !== undefined) {
      closeModal();
      setSidebarOpen(newState!);
    } else {
      newState = !isSidebarOpen;
      setSidebarOpen(!isSidebarOpen);
    }

    if (isMobile) {
      if (newState === true) {
        document.body.className = `overflow-hidden sm:overflow-auto`;
      } else {
        document.body.className = `overflow-auto`;
      }
    }
  };
  // Drawer
  const [isLeftDrawerOpen, setLeftDrawerOpen, isLeftDrawerOpenRef] =
    useStateRef(false);

  const [isRightDrawerOpen, setRightDrawerOpen, isRightDrawerOpenRef] =
    useStateRef(false);

  const [leftDrawerData, setLeftDrawerData] = React.useState(initialContent);
  const [rightDrawerData, setRightDrawerData] = React.useState(initialContent);

  const openLeftDrawer = React.useCallback((data: InitialDataInterface) => {
    setLeftDrawerData(data);
    if (isRightDrawerOpenRef) setRightDrawerOpen(false);
    if (isSidebarOpenRef.current) toggleSidebar();
    setLeftDrawerOpen(true);
    setOverlayOpen(true);
  }, []);

  const openRightDrawer = React.useCallback((data: InitialDataInterface) => {
    setRightDrawerData(data);
    if (isLeftDrawerOpenRef) setLeftDrawerOpen(false);
    if (isSidebarOpenRef.current) toggleSidebar();
    setRightDrawerOpen(true);
    setOverlayOpen(true);

    listenersRef.current = {
      ...listenersRef.current,
      escapeRightDrawer: (e: KeyboardEvent) =>
        handleEscape(e, closeRightDrawer),
    };

    document.addEventListener(
      "keydown",
      listenersRef.current.escapeRightDrawer,
    );
  }, []);

  const closeLeftDrawer = React.useCallback(() => {
    setLeftDrawerOpen(false);
    setOverlayOpen(false);
  }, []);

  const overlayFunction = React.useRef(() => () => {});

  const closeRightDrawer = React.useCallback(() => {
    setRightDrawerData(initialContent);
    setRightDrawerOpen(false);
    setOverlayOpen(false);

    document.removeEventListener(
      "keydown",
      listenersRef.current.escapeRightDrawer,
    );
  }, []);

  const resetAll = React.useCallback(() => {
    setOverlayOpen(false);
    closeRightDrawer();
    closeLeftDrawer();
    closeModal();
    closeActionBar();
    closeNestedModal();
    toggleSidebar(false);
  }, []);

  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const leftRef = isLeftDrawerOpenRef.current;
    const rightRef = isRightDrawerOpenRef.current;
    const sidebarRef = isSidebarOpenRef.current;
    if (currentRouteOptions.closeDrawerOnRouteChange) closeRightDrawer();

    return () => {
      if (sidebarRef) toggleSidebar(false);
      if (leftRef) closeLeftDrawer();

      if (rightRef && currentRouteOptions.closeDrawerOnRouteChange)
        closeRightDrawer();
    };
  }, [pathname]);

  const contextValue = {
    isOverlayOpen,
    setOverlayOpen,
    modalData,
    openModal,
    closeModal,
    nestedModalData,
    isNestedModalOpen,
    openNestedModal,
    closeNestedModal,
    isModalOpen,
    isSidebarOpen,
    toggleSidebar,
    sidebarData,
    setSidebarData,
    isLeftDrawerOpen,
    isRightDrawerOpen,
    leftDrawerData,
    rightDrawerData,
    openLeftDrawer,
    openRightDrawer,
    closeLeftDrawer,
    closeRightDrawer,
    resetAll,
    currentRouteOptions,
    setRouteOptions: (options: currentRouteOptionsI) => {
      setCurrentRouteOptions((prev) => Object.assign(prev, options));
    },
    resetCurrentRouteOptions,
    overlayFunction,
    isSidebarShrunk,
    setSidebarShrunk,
    isActionBarOpen,
    openActionBar,
    closeActionBar,
    actionBarData,
  };

  return (
    <UIContext.Provider value={contextValue} test-id="0m1gbyUb">
      {children}
    </UIContext.Provider>
  );
};

export default UIProvider;
